import { BaseText } from '@/src/components/BaseText'
import type { AdjustmentRow } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/table/adjustmentTable.types'
import { useAdjustmentConsumptionInjector } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/table/useAdjustmentConsumptionInjector.hook'
import useTableSort from '@/src/hooks/useTableSort.hook'
import { Flex, Table, TableContainer, Tbody, Th, Thead, Tooltip, Tr, chakra } from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadPlanningAdjustmentTableRow } from './LoadPlanningAdjustment.tableRow'

const ColGroup = chakra('colgroup')
const Col = chakra('col')

const getColumnHeaders = (
  t: TFunction
): {
  title: string
  sortable?: boolean
  sortKey?: keyof AdjustmentRow
  width: string
  minWidth: string
  align?: 'left' | 'right' | 'center'
}[] => {
  return [
    {
      title: t('components.procurement.forecasting.adjustment_table.column_name'),
      sortable: true,
      sortKey: 'name',
      width: '300px',
      minWidth: '180px',
    },
    {
      title: t('components.procurement.forecasting.adjustment_table.column_type'),
      sortable: true,
      sortKey: 'type',
      width: '200px',
      minWidth: '140px',
    },
    {
      title: t('components.procurement.forecasting.adjustment_table.column_location'),
      sortable: true,
      sortKey: 'locationId',
      width: '200px',
      minWidth: '140px',
    },
    {
      title: t('components.procurement.forecasting.adjustment_table.column_delta'),
      sortable: true,
      sortKey: 'consumptionDelta',
      width: '200px',
      minWidth: '200px',
      align: 'right' as const,
    },
    {
      title: '',
      sortable: false,
      width: '60px',
      minWidth: '60px',
    },
  ]
}

export const LoadPlanningAdjustmentTable = ({ rows }: { rows: AdjustmentRow[] }) => {
  const { t } = useTranslation()

  const { adjustments, isInjecting } = useAdjustmentConsumptionInjector({ adjustments: rows })

  const columnHeaders = useMemo(() => getColumnHeaders(t), [t])

  const { sortedData, SortArrows } = useTableSort(adjustments, 'name')

  return (
    <TableContainer overflowX='visible'>
      <Table
        variant='simple'
        size='sm'
        sx={{
          tableLayout: 'fixed',
          minWidth: '940px',
          '& th, & td': { overflow: 'hidden' },
          '& th p': { whiteSpace: 'nowrap' },
          '& th > div': { minWidth: 0 },
        }}
      >
        <ColGroup>
          {columnHeaders.map((header, index) => (
            <Col key={`${header.title}-${index}`} sx={{ width: header.width, minWidth: header.minWidth }} />
          ))}
        </ColGroup>
        <Thead>
          <Tr>
            {columnHeaders.map((header, index) => (
              <Th key={`${String(header.sortKey)}-${index}`} width={header.width} minWidth={header.minWidth} textTransform='none'>
                <Flex gap={2} justifyContent={header.align === 'right' ? 'flex-end' : 'flex-start'} alignItems={'center'} minW={0}>
                  <Tooltip
                    label={header.title}
                    bg='white'
                    color='gray.800'
                    fontWeight='normal'
                    borderRadius='base'
                    hasArrow
                    openDelay={300}
                    isDisabled={!header.title}
                  >
                    <BaseText fontSize={'small'} overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap' minW={0}>
                      {header.title}
                    </BaseText>
                  </Tooltip>
                  {header.sortKey && <SortArrows headKey={String(header.sortKey)} />}
                </Flex>
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {sortedData.map(row => (
            <LoadPlanningAdjustmentTableRow key={row.id} row={row} isInjecting={isInjecting} />
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
